import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { planEntryLabel } from 'src/utils/plan/plan-public-ladder';

import { Iconify } from 'src/components/iconify';

import { useHomeReveal } from '../hooks/model/use-home-reveal';
import { CM, revealSx, glueWords, SECTION_PX, PUBLIC_TEXT, PUBLIC_MAX_WIDTH } from '../constants/home-theme';

import type { HomeScene } from '../constants/home-copy';

// ----------------------------------------------------------------------

/**
 * 홈 장면 한 칸의 틀 — 메뉴 이름 · 제목 두 줄 · 설명 한 줄 · 플랜 · 「자세히」, 그리고 실제 화면 조각(`children`).
 *
 * 🔑 글은 조각보다 작게 선다 — 넓은 화면은 왼쪽 좁은 칸(`split`) 또는 위 한 줄(`stack` · 제목 왼쪽 · 설명 오른쪽), 폰은 늘 위.
 * 🔑 조각은 화면에 들어오면 살짝 떠오른다(`revealSx`) — 들어오기 전에도 보인다.
 *   장면 안 연출(정렬 바꾸기 등)이 같은 신호를 쓰도록 `children` 에 함수를 줄 수 있다.
 * @param layout `split` 글 왼쪽 · 조각 오른쪽 / `stack` 글 위 · 조각 아래 전체 폭
 * @param tinted 옅은 바탕 — 장면이 번갈아 바탕을 바꿔 경계가 읽히게 한다
 */
export function HomeSceneFrame({
  scene,
  layout = 'split',
  tinted = false,
  children,
}: {
  scene: HomeScene;
  layout?: 'split' | 'stack';
  tinted?: boolean;
  children: ReactNode | ((shown: boolean) => ReactNode);
}) {
  const reveal = useHomeReveal();
  const split = layout === 'split';
  const plans = scene.plans
    .map((plan) => [plan.label, planEntryLabel(plan.field)].filter(Boolean).join(' '))
    .join(' · ');

  return (
    <Box id={`scene-${scene.id}`} data-section-id={`scene-${scene.id}`} component="section" sx={{ px: SECTION_PX, py: { xs: 5, md: 10 }, bgcolor: tinted ? CM.bgSection : '#fff' }}>
      <Box
        sx={{
          maxWidth: PUBLIC_MAX_WIDTH,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: split ? '300px minmax(0, 1fr)' : 'minmax(0, 1fr)' },
          gap: { xs: 2.5, md: split ? 6 : 3 },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: split ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)' },
            columnGap: 6,
            alignItems: 'end',
          }}
        >
          <Box>
            <Typography sx={{ ...PUBLIC_TEXT.eyebrow, color: CM.primary, mb: 1 }}>{scene.menu}</Typography>
            <Typography component="h2" sx={{ ...PUBLIC_TEXT.title, color: CM.textDark, mb: { xs: 1.5, md: split ? 1.5 : 0 } }}>
              {scene.title.split('\n').map((line, i) => (
                <Box component="span" key={line}>
                  {i > 0 && <Box component="br" sx={{ display: { xs: 'none', sm: 'inline' } }} />}
                  {i > 0 && <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}> </Box>}
                  {glueWords(line)}
                </Box>
              ))}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ ...PUBLIC_TEXT.body, fontSize: { xs: 15, md: 16 }, color: CM.textMuted, mb: 1 }}>{glueWords(scene.lead)}</Typography>
            <Stack direction="row" columnGap={2} useFlexGap flexWrap="wrap" alignItems="center">
              {/* 폰은 플랜 줄을 접는다 — 장면이 한 화면에 들도록(플랜은 아래 요금 구역이 다시 보인다) */}
              {plans && <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 13, color: CM.textSubtle }}>{plans}</Typography>}
              <SceneLink href={`${paths.home.features}#${scene.job}`} label="자세히 보기" />
              {scene.also && <SceneLink href={`${paths.home.features}#${scene.also.job}`} label={scene.also.label} hideOnPhone />}
            </Stack>
          </Box>
        </Box>

        <Box ref={reveal.ref} sx={{ minWidth: 0, ...revealSx(reveal.shown, reveal.reduceMotion) }}>
          {typeof children === 'function' ? children(reveal.shown) : children}
        </Box>
      </Box>
    </Box>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 「자세히」 — 글자 링크지만 엄지가 닿는 높이(44px)를 준다 */
function SceneLink({ href, label, hideOnPhone = false }: { href: string; label: string; hideOnPhone?: boolean }) {
  return (
    <Link
      component={RouterLink}
      href={href}
      underline="none"
      sx={{ display: { xs: hideOnPhone ? 'none' : 'inline-flex', sm: 'inline-flex' }, alignItems: 'center', gap: 0.5, minHeight: 44, fontSize: 14, fontWeight: 600, color: CM.primary }}
    >
      {label}
      <Iconify icon="solar:alt-arrow-right-linear" width={16} />
    </Link>
  );
}
